/**
 * Small building blocks shared by the inventory tabs (toolbar search, segmented views, filter
 * chips with a picker, the table shell + pager, the floating bulk bar, callouts, location tags).
 * They sit on the app foundation (FilterChip, Button, tokens) — no page-local colours.
 */
import { forwardRef, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { Check, Download, FileSpreadsheet, FileText, Minus, SquareCheck, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SearchField as UiSearchField } from '@/components/ui/search-field';
import { ChipSelect as UiChipSelect } from '@/components/ui/chip-select';
import { Pager as UiPager } from '@/components/ui/pager';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { formatNumber } from '@/lib/format';
import { locationColor, locationShort, personColor, personInitials, formatQty } from './inventoryModel';

/* ------------------------------------------------------------------ layout */

/** True while the media query matches (re-renders on change). False where matchMedia is missing. */
export function useMediaQuery(query) {
  const read = () => (typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? Boolean(window.matchMedia(query)?.matches)
    : false);
  const [matches, setMatches] = useState(read);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const list = window.matchMedia(query);
    if (!list) return undefined;
    const update = () => setMatches(Boolean(list.matches));
    update();
    if (list.addEventListener) list.addEventListener('change', update); else list.addListener?.(update);
    return () => {
      if (list.removeEventListener) list.removeEventListener('change', update); else list.removeListener?.(update);
    };
  }, [query]);
  return matches;
}

/**
 * [ref, width] — the element's current width in px (0 until measured, or where ResizeObserver is
 * missing). Layouts that depend on the space a card really gets (not the window) use this.
 */
export function useElementWidth() {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);
  // Measured before the first paint, so a layout that depends on it does not flash.
  useLayoutEffect(() => {
    const node = ref.current;
    if (!node || typeof window === 'undefined' || typeof window.ResizeObserver !== 'function') return undefined;
    const update = () => setWidth(Math.round(node.getBoundingClientRect().width));
    update();
    const observer = new window.ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
}

/**
 * A row that scrolls sideways when its content does not fit (phone filter chips, a view control squeezed on
 * a toolbar), with a fade on the right edge while more is hidden there. Scrollbar hidden; touch / wheel /
 * Shift+wheel scroll it.
 */
export function ScrollFade({ className, children, testId, ...props }) {
  const ref = useRef(null);
  // Which edges hide more content: 'right', 'left', 'both' or '' (nothing hidden).
  const [fade, setFade] = useState('');
  const update = useCallback(() => {
    const node = ref.current;
    if (!node) return;
    const right = node.scrollLeft + node.clientWidth < node.scrollWidth - 1;
    const left = node.scrollLeft > 1;
    setFade(left && right ? 'both' : left ? 'left' : right ? 'right' : '');
  }, []);
  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    // The chosen option (aria-pressed) is never left hidden behind the fade.
    const active = node.querySelector('[aria-pressed="true"]');
    if (active && node.scrollWidth > node.clientWidth) {
      const box = node.getBoundingClientRect();
      const option = active.getBoundingClientRect();
      if (option.right > box.right - 8) node.scrollLeft += option.right - box.right + 40;
      else if (option.left < box.left) node.scrollLeft -= box.left - option.left + 8;
    }
    update();
    if (typeof window === 'undefined' || typeof window.ResizeObserver !== 'function') return undefined;
    const observer = new window.ResizeObserver(update);
    observer.observe(node);
    [...node.children].forEach((child) => observer.observe(child));
    return () => observer.disconnect();
  }, [update, children]);
  return (
    <div
      ref={ref}
      onScroll={update}
      className={cn(
        'min-w-0 max-w-full overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
        fade === 'right' && '[mask-image:linear-gradient(to_right,#000_calc(100%-36px),transparent)]',
        fade === 'left' && '[mask-image:linear-gradient(to_right,transparent,#000_28px)]',
        fade === 'both' && '[mask-image:linear-gradient(to_right,transparent,#000_28px,#000_calc(100%-36px),transparent)]',
        className
      )}
      data-fade={fade || 'none'}
      data-testid={testId}
      {...props}
    >
      {children}
    </div>
  );
}

/* ------------------------------------------------------------------ cards */

export function InvCard({ className, children, ...props }) {
  return (
    <section className={cn('min-w-0 rounded-card border border-line bg-surface text-ink shadow-card', className)} {...props}>
      {children}
    </section>
  );
}

export function CardHead({ icon: Icon, iconClassName, title, badge, right, className, titleAs: TitleTag = 'h3' }) {
  return (
    <div className={cn('flex min-h-[52px] items-center justify-between gap-3 border-b border-line-2 px-5 py-3', className)}>
      <div className="flex min-w-0 items-center gap-2">
        {Icon ? <Icon className={cn('h-4 w-4 shrink-0 text-ink-3', iconClassName)} aria-hidden="true" /> : null}
        <TitleTag className="truncate font-heading text-[15px] font-bold text-ink">{title}</TitleTag>
        {badge}
      </div>
      {right ? <div className="flex shrink-0 items-center gap-2">{right}</div> : null}
    </div>
  );
}

export function CardFoot({ className, children }) {
  return (
    <div className={cn('flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-line-2 px-5 py-3 text-[12.5px] text-ink-3', className)}>
      {children}
    </div>
  );
}

export function UpperLabel({ className, children }) {
  return <p className={cn('text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3', className)}>{children}</p>;
}

export function LinkButton({ className, children, ...props }) {
  return (
    <button
      type="button"
      className={cn('inline-flex items-center gap-1 rounded text-[13px] font-semibold text-brand-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring', className)}
      {...props}
    >
      {children}
    </button>
  );
}

/** Small count pill used next to card titles and in lists. */
export function CountPill({ tone = 'gray', className, children }) {
  return (
    <span className={cn(`tone-${tone}`, 'inline-flex h-5 min-w-[22px] items-center justify-center rounded-full bg-[color:var(--tone-bg)] px-1.5 text-[11.5px] font-bold tabular-nums text-[color:var(--tone-ink)]', className)}>
      {children}
    </span>
  );
}

/* ------------------------------------------------------------------ toolbar */

/** Toolbar search with a "/" shortcut (focuses the box from anywhere on the page). */
/** Toolbar search — the shared one (components/ui/search-field); "/" focuses it. */
export const SearchField = forwardRef(function SearchField({ testId = 'search-inventory', ...props }, ref) {
  return <UiSearchField ref={ref} testId={testId} {...props} />;
});

/** Segmented control: one of N views (components/ui/segmented). */
export { Segmented } from '@/components/ui/segmented';

/** Toolbar filter chip with a picker: dashed "+ Brand" when unset (components/ui/chip-select). */
export function ChipSelect(props) {
  return <UiChipSelect variant="add" emptyText="No values" popoverClassName="w-64" {...props} />;
}

/** Export menu: Excel / CSV / PDF (print) for whatever the tab is showing. */
export function ExportMenu({ onExcel, onCsv, onPdf, disabled, testId, label = 'Export', iconOnly = false }) {
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size={iconOnly ? 'icon-sm' : 'sm'}
          disabled={disabled}
          data-testid={testId}
          className="text-ink"
          aria-label={iconOnly ? label : undefined}
          title={iconOnly ? label : undefined}
        >
          <Download aria-hidden="true" />{iconOnly ? null : ` ${label}`}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[200px] rounded-xl border-line bg-surface p-1.5 text-ink shadow-overlay">
        {onExcel ? (
          <DropdownMenuItem onSelect={() => setTimeout(onExcel, 0)} className="h-[34px] gap-2.5 rounded-lg text-[13px]">
            <FileSpreadsheet className="h-[15px] w-[15px] text-ink-3" aria-hidden="true" /> Excel (.xlsx)
          </DropdownMenuItem>
        ) : null}
        {onCsv ? (
          <DropdownMenuItem onSelect={() => setTimeout(onCsv, 0)} className="h-[34px] gap-2.5 rounded-lg text-[13px]">
            <FileText className="h-[15px] w-[15px] text-ink-3" aria-hidden="true" /> CSV
          </DropdownMenuItem>
        ) : null}
        {onPdf ? (
          <DropdownMenuItem onSelect={() => setTimeout(onPdf, 0)} className="h-[34px] gap-2.5 rounded-lg text-[13px]">
            <FileText className="h-[15px] w-[15px] text-ink-3" aria-hidden="true" /> PDF (print view)
          </DropdownMenuItem>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/* ------------------------------------------------------------------ table */

/**
 * Table shell. On wide screens (xl) the table is not inside a scroll box, so its header sticks to the
 * window under the top bar; below xl it scrolls sideways instead.
 */
export function TableWrap({ className, children, ...props }) {
  return (
    <div className={cn('min-w-0 rounded-card border border-line bg-surface shadow-card', className)} {...props}>
      {children}
    </div>
  );
}

export function TableScroll({ className, children }) {
  return <div className={cn('overflow-x-auto xl:overflow-x-visible', className)}>{children}</div>;
}

export const tableClass = 'w-full border-separate border-spacing-0 text-[13px] text-ink';
export const thClass = 'sticky top-0 z-[5] h-9 whitespace-nowrap border-b border-line bg-surface-2 px-3 text-left text-[11.5px] font-bold tracking-[0.02em] text-ink-3 xl:top-[var(--app-header-height)] first:rounded-tl-card last:rounded-tr-card';
export const tdClass = 'h-11 border-b border-line-2 px-3 align-middle';
export const numClass = 'text-right tabular-nums';
export const subClass = 'mt-px block text-[11.5px] text-ink-3';

export function InvCheckbox({ className, indeterminate, checked, ...props }) {
  const state = indeterminate ? 'indeterminate' : checked;
  return (
    <CheckboxPrimitive.Root
      checked={state}
      className={cn(
        'peer grid h-4 w-4 shrink-0 place-items-center rounded-[4px] border-[1.5px] border-[color:var(--ink-4)] bg-surface text-white transition-colors',
        'hover:border-[color:var(--ink-3)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-40',
        'data-[state=checked]:border-brand data-[state=checked]:bg-brand data-[state=indeterminate]:border-brand data-[state=indeterminate]:bg-brand',
        className
      )}
      {...props}
    >
      <CheckboxPrimitive.Indicator>
        {indeterminate ? <Minus className="h-3 w-3" strokeWidth={3} aria-hidden="true" /> : <Check className="h-3 w-3" strokeWidth={3} aria-hidden="true" />}
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
}

/** ‹ 1 … 4 5 6 … 15 › (components/ui/pager, bordered cells). */
export function Pager(props) {
  return <UiPager variant="outline" {...props} />;
}

export function TableFoot({ left, page, pageCount, onPage, pageSize, pageSizes = [25, 50, 100], onPageSize, children, className }) {
  return (
    <div className={cn('flex flex-wrap items-center justify-between gap-3 rounded-b-card border-t border-line-2 bg-surface px-3.5 py-2.5 text-[12.5px] text-ink-3', className)}>
      <div className="min-w-0">{left}</div>
      <div className="flex flex-wrap items-center gap-3">
        {children}
        {onPageSize ? (
          <label className="flex items-center gap-2">
            <span>Rows per page</span>
            <select
              value={pageSize}
              onChange={(event) => onPageSize(Number(event.target.value))}
              className="h-[26px] rounded-[7px] border border-line bg-surface px-1.5 text-[12px] font-semibold text-ink-2 outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {pageSizes.map((size) => <option key={size} value={size}>{size}</option>)}
            </select>
          </label>
        ) : null}
        {onPage ? <Pager page={page} pageCount={pageCount} onPage={onPage} /> : null}
      </div>
    </div>
  );
}

/**
 * Floating navy bar for a table selection. Sticks to the bottom of the window while the table
 * scrolls and rests over the table's bottom edge at the end.
 */
export function BulkBar({ count, noun = 'selected', onClear, children, testId = 'inventory-bulkbar' }) {
  if (!count) return null;
  return (
    <div className="pointer-events-none sticky bottom-4 z-20 -mt-[22px] flex justify-center px-2" data-testid={testId}>
      <div
        role="toolbar"
        aria-label={`${count} ${noun}`}
        className="pointer-events-auto flex max-w-full flex-wrap items-center gap-1.5 rounded-xl bg-brand py-2 pl-3.5 pr-2 text-[13px] font-semibold text-white shadow-overlay"
      >
        <span className="mr-1 inline-flex items-center gap-2 whitespace-nowrap">
          <SquareCheck className="h-[15px] w-[15px]" aria-hidden="true" />
          {formatNumber(count, { maxDecimals: 0 })} {noun}
        </span>
        {children}
        <span className="mx-1 h-5 w-px bg-white/25" aria-hidden="true" />
        <BulkButton onClick={onClear} icon={X}>Clear</BulkButton>
      </div>
    </div>
  );
}

export const BulkButton = forwardRef(function BulkButton({ icon: Icon, children, className, ...props }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      className={cn(
        'inline-flex h-[30px] items-center gap-1.5 whitespace-nowrap rounded-lg bg-white/10 px-2.5 text-[12.5px] font-semibold text-white transition-colors hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60 disabled:opacity-50',
        className
      )}
      {...props}
    >
      {Icon ? <Icon className="h-3.5 w-3.5" aria-hidden="true" /> : null}
      {children}
    </button>
  );
});

/* ------------------------------------------------------------------ bits */

/** `subWrap`: the sub line may take two lines (a list of part numbers that must be read in full). */
export function Callout({ tone = 'navy', icon: Icon, title, sub, subWrap = false, action, className, children, ...props }) {
  const toneClasses = {
    navy: 'border-[color:var(--navy-100)] bg-[color:var(--navy-50)]',
    amber: 'border-[color:var(--status-amber-bg)] bg-status-amber-bg',
    gray: 'border-line bg-surface'
  };
  return (
    <div className={cn('flex min-w-0 items-center gap-3 rounded-xl border px-3.5 py-3 text-[13px] text-ink', toneClasses[tone] || toneClasses.gray, className)} {...props}>
      {Icon ? (
        <span className={cn('ui-icon-tile h-9 w-9', tone === 'amber' ? 'tone-amber bg-surface' : 'tone-navy bg-surface')} aria-hidden="true">
          <Icon className="h-[17px] w-[17px]" />
        </span>
      ) : null}
      <div className="min-w-0 flex-1">
        <div className="text-[13px] text-ink">{title}</div>
        {sub ? <div className={cn('mt-0.5 text-[12px] text-ink-2', subWrap ? 'line-clamp-2' : 'truncate')}>{sub}</div> : null}
        {children}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}

export function LocationTag({ location, qty, className }) {
  return (
    <span
      className={cn('inline-flex h-[22px] items-center gap-1.5 whitespace-nowrap rounded-md border border-line bg-surface-2 px-2 text-[11.5px] font-semibold text-ink-2', className)}
      title={location}
    >
      <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: locationColor(location) }} aria-hidden="true" />
      {locationShort(location)}
      {qty !== undefined ? <b className="font-bold tabular-nums text-ink">{formatQty(qty)}</b> : null}
    </span>
  );
}

export function ProgressBar({ value = 0, tone = 'navy', className, label }) {
  const pct = Math.max(0, Math.min(100, Number(value) || 0));
  const fills = {
    navy: 'bg-brand',
    green: 'bg-status-green',
    amber: 'bg-status-amber',
    blue: 'bg-status-blue',
    violet: 'bg-status-violet'
  };
  return (
    <span
      role="progressbar"
      aria-label={label}
      aria-valuenow={Math.round(pct)}
      aria-valuemin={0}
      aria-valuemax={100}
      className={cn('block h-1.5 min-w-[40px] overflow-hidden rounded-full bg-[color:var(--line)]', className)}
    >
      <span className={cn('block h-full rounded-full', fills[tone] || fills.navy)} style={{ width: `${pct}%` }} />
    </span>
  );
}

export function PersonAvatar({ name, size = 22, className }) {
  return (
    <span
      className={cn('inline-grid shrink-0 place-items-center rounded-full font-bold text-white', className)}
      style={{ width: size, height: size, fontSize: Math.max(9, Math.round(size * 0.42)), background: personColor(name) }}
      aria-hidden="true"
    >
      {personInitials(name)}
    </span>
  );
}
